import React, { useState, FormEvent } from 'react'
import { useNavigate } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { CircularProgress } from '@mui/material'
import { AuthShell } from './AuthShell'

/** Full-page forgot-password flow (HTML redesign). */
const ForgotPasswordPage: React.FC = () => {
  const { t } = useTranslation()
  const navigate = useNavigate()
  const [email, setEmail] = useState('')
  const [loading, setLoading] = useState(false)
  const [done, setDone] = useState(false)

  const handleSubmit = (e: FormEvent) => {
    e.preventDefault()
    if (!email.trim()) return
    setLoading(true)
    setTimeout(() => {
      setLoading(false)
      setDone(true)
    }, 900)
  }

  const handleResend = () => {
    setLoading(true)
    setTimeout(() => {
      setLoading(false)
      setDone(true)
    }, 900)
  }

  return (
    <AuthShell>
      {!done ? (
        <>
          <button
            type="button"
            className="incyia-auth-link incyia-auth-back"
            onClick={() => navigate('/login')}
          >
            <svg
              viewBox="0 0 16 16"
              fill="none"
              stroke="currentColor"
              strokeWidth="1.6"
              aria-hidden
            >
              <path d="M10 3.5L5.5 8 10 12.5" strokeLinecap="round" />
            </svg>
            {t('auth.backToSignIn')}
          </button>

          <h1 className="incyia-auth-title">{t('auth.resetYourPassword')}</h1>
          <p className="incyia-auth-sub">{t('auth.resetYourPasswordSub')}</p>

          <form onSubmit={handleSubmit}>
            <div className="incyia-auth-field">
              <label className="incyia-auth-label" htmlFor="forgot-email">
                {t('auth.email')}
                <span className="incyia-auth-req">*</span>
              </label>
              <input
                id="forgot-email"
                className="incyia-auth-inp"
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder={t('auth.emailPlaceholder')}
                autoComplete="email"
                required
              />
            </div>

            <button
              type="submit"
              className="incyia-auth-submit"
              disabled={loading}
            >
              {loading ? (
                <CircularProgress size={20} sx={{ color: '#fff' }} />
              ) : (
                t('auth.sendResetLink')
              )}
            </button>
          </form>

          <div className="incyia-auth-trail">
            <svg
              viewBox="0 0 16 16"
              fill="none"
              stroke="currentColor"
              strokeWidth="1.5"
              aria-hidden
            >
              <circle cx="8" cy="8" r="5.5" />
              <path d="M8 7.2v3.5M8 5.2h.01" strokeLinecap="round" />
            </svg>
            <span>{t('auth.resetPrivacyNote')}</span>
          </div>
        </>
      ) : (
        <>
          <span className="incyia-auth-ok" aria-hidden>
            <svg
              viewBox="0 0 16 16"
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
            >
              <path d="M3.5 8.5l3 3 6-7" />
            </svg>
          </span>
          <h1 className="incyia-auth-title">{t('auth.checkInbox')}</h1>
          <p className="incyia-auth-sub">{t('auth.checkInboxSub')}</p>

          <div className="incyia-auth-help-box">
            <b>{t('auth.nothingArrived')}</b>
            <ul>
              <li>{t('auth.helpSpam')}</li>
              <li>{t('auth.helpTypo')}</li>
              <li>{t('auth.helpAdmin')}</li>
            </ul>
          </div>

          <button
            type="button"
            className="incyia-auth-submit is-ghost"
            onClick={handleResend}
            disabled={loading}
          >
            {loading ? (
              <CircularProgress size={20} />
            ) : (
              t('auth.sendAgain')
            )}
          </button>

          <button
            type="button"
            className="incyia-auth-link incyia-auth-center-link"
            onClick={() => navigate('/login')}
          >
            {t('auth.backToSignIn')}
          </button>
        </>
      )}
    </AuthShell>
  )
}

export default ForgotPasswordPage
